import axios, { InternalAxiosRequestConfig } from "axios";
import { countryFromNextUrl, refreshNextSession } from "./nextApi";

// Setup global de axios para que las requests al proxy de Next lleven la
// cookie httpOnly automáticamente, y para limpiar `Authorization: Bearer `
// cuando el token está vacío (caso customer post-migración: el token vive en
// la cookie y los services legacy aún hardcodean el header).

let configured = false;

export const setupAxios = () => {
  if (configured) return;
  configured = true;

  axios.interceptors.request.use((config) => {
    const url = config.url || "";
    const goesToNext = url.includes("/api/proxy/") || url.includes("/api/auth/");

    // Cuando hablamos con Next (proxy o auth), mandamos cookies. Sin esto, el
    // proxy no puede leer propi_session y responde 401.
    if (goesToNext) {
      config.withCredentials = true;
    }

    // Si los services pasaron `Authorization: Bearer ` con token vacío
    // (porque user.token es '' en el flujo de cookies), removemos el header
    // para que Next pueda extraer el token desde la cookie.
    const headers = config.headers as any;
    if (headers) {
      const auth = headers["Authorization"] ?? headers["authorization"];
      if (typeof auth === "string" && auth.trim().toLowerCase() === "bearer") {
        delete headers["Authorization"];
        delete headers["authorization"];
      }
    }

    return config;
  });

  // 401 del proxy de Next (sesión customer por cookies): el access token
  // (propi_session, ~10 min) puede haber vencido con la sesión todavía viva.
  // Antes de que el caller lo trate como logout, intentamos refrescar las
  // cookies y reintentamos el request original UNA sola vez. Si el refresh
  // falla, propagamos el 401 original y el caller sigue con su flujo de
  // logout de siempre.
  //
  // Sólo aplica a /api/proxy/: los realms legacy (token+rft en
  // localStorage) le pegan directo al backend y no pasan por acá.
  axios.interceptors.response.use(undefined, async (error) => {
    const config = error?.config as (InternalAxiosRequestConfig & { _propiRetried?: boolean }) | undefined;
    const status = error?.response?.status;
    const url = config?.url || "";

    if (!config || status !== 401 || config._propiRetried || !url.includes("/api/proxy/")) {
      return Promise.reject(error);
    }

    const country = countryFromNextUrl(url);
    if (!country) return Promise.reject(error);

    const refreshed = await refreshNextSession(country);
    if (!refreshed) return Promise.reject(error);

    // Marcamos el config para no volver a refrescar si el retry también da 401.
    config._propiRetried = true;
    return axios(config);
  });
};
